import React from 'react';

import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';

import { SectionIconWrapper } from './components';
import { COMPONENT_REGISTRY, REPORT_TYPES, VALID_COMPONENT_TYPES } from './constants';
import * as Styled from './ReportSection.style';
import { reportMarkdownComponents } from '../../utils/markdownComponents';

/**
 * Individual report section component with icon, title, and content
 * Supports both standard report sections and cost-specific sections
 * @param {object} props - Component props
 * @param {React.ReactElement} props.icon - Icon component for the section
 * @param {string} props.title - Section title
 * @param {string|object|Array} props.content - Section content (string, JSON object, or array)
 * @param {string} [props.component='markdown'] - Component type from COMPONENT_REGISTRY
 * @param {Array} [props.subsections=[]] - Array of subsections with {title, component, content}
 * @param {string} [props.reportType='idea'] - Report type: 'idea', 'name', 'cost', or 'market'
 * @param {string} [props.costRange] - Cost range string (e.g., "$300 - $550") for cost reports
 * @param {boolean} [props.isBubble=false] - Whether section should be displayed as a bubble
 * @param {boolean} [props.showDisclaimer=false] - Whether to show disclaimer instead of content
 * @param {string} [props.disclaimer=''] - Disclaimer text to display
 */
function ReportSection({
  icon,
  title,
  content,
  component = 'markdown',
  subsections = [],
  reportType = 'idea',
  costRange,
  isBubble = false,
  showDisclaimer = false,
  disclaimer = ''
}) {
  // Render content based on component type using registry
  const renderContent = () => {
    // Cost report with disclaimer
    if (showDisclaimer && disclaimer) {
      return <ReactMarkdown components={reportMarkdownComponents}>{disclaimer}</ReactMarkdown>;
    }

    // Cost report with string content
    if (reportType === 'cost' && typeof content === 'string') {
      return <ReactMarkdown components={reportMarkdownComponents}>{content}</ReactMarkdown>;
    }

    // Standard reports - use component registry
    const renderer = COMPONENT_REGISTRY[component] || COMPONENT_REGISTRY.markdown;
    return renderer(content, { isBubble });
  };

  // Render subsections if present
  const renderSubsections = () => {
    if (!subsections || subsections.length === 0) {
      return null;
    }

    return subsections.map((subsection, index) => {
      const subsectionRenderer =
        COMPONENT_REGISTRY[subsection.component] || COMPONENT_REGISTRY.markdown;

      return (
        <Styled.SubsectionWrapper key={index}>
          {subsection.title && (
            <Styled.SubsectionTitle mb={1} size="sm" weight="medium">
              {subsection.title}
            </Styled.SubsectionTitle>
          )}
          <Styled.SubsectionContent>
            {subsectionRenderer(subsection.content, { isBubble })}
          </Styled.SubsectionContent>
        </Styled.SubsectionWrapper>
      );
    });
  };

  // Standard report layout (idea/name/market/cost reports)
  return (
    <Styled.ReportSectionRoot isBubble={isBubble}>
      <SectionIconWrapper icon={icon} placement="desktop" />
      <Styled.SectionHeader isbubble={isBubble ? 'true' : undefined}>
        <Styled.SectionTitleSection>
          <SectionIconWrapper icon={icon} placement="mobile" />
          <Styled.SectionTitle mb={1} size="md">
            {title}
          </Styled.SectionTitle>
          {costRange && <Styled.CostRange size="sm">{costRange}</Styled.CostRange>}
        </Styled.SectionTitleSection>

        <Styled.SectionContent size="md">
          {renderContent()}
          {renderSubsections()}
        </Styled.SectionContent>
      </Styled.SectionHeader>
    </Styled.ReportSectionRoot>
  );
}

ReportSection.propTypes = {
  icon: PropTypes.element,
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
  component: PropTypes.oneOf(VALID_COMPONENT_TYPES),
  subsections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      component: PropTypes.oneOf(VALID_COMPONENT_TYPES),
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]).isRequired
    })
  ),
  reportType: PropTypes.oneOf(REPORT_TYPES),
  costRange: PropTypes.string,
  isBubble: PropTypes.bool,
  showDisclaimer: PropTypes.bool,
  disclaimer: PropTypes.string
};

export default ReportSection;
